// src/components/dashboard/TipOfTheDay.jsx
//
// One tip a day, to read or not. Reading it used to earn "+5 XP" and count
// towards a streak, and a "Read" tick was kept in this browser to stop the
// points being claimed twice. The points, the streak and the tick went in
// October 2026 (docs/REVIEW_2026-10.md, Decision 1).
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Share2, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../shared/CardComponents';
import Button from '../shared/Button';
import { getPersonalizedTip } from '../../data/friendshipTips';

const TipOfTheDay = ({ completedModules = [], setCurrentView }) => {
  const [tip, setTip] = useState(null);
  const [showShareSuccess, setShowShareSuccess] = useState(false);

  useEffect(() => {
    // Today's tip, leaning towards the modules you have finished.
    setTip(getPersonalizedTip(completedModules));
  }, [completedModules]);

  const handleShare = () => {
    if (!tip) return;

    const shareText = `${tip.tip}\n\n${tip.action}\n\n- Friendship Vibe 💜`;

    if (navigator.share) {
      navigator.share({
        title: tip.title,
        text: shareText
      }).catch(() => {
        // Fallback to clipboard
        navigator.clipboard.writeText(shareText);
        setShowShareSuccess(true);
        setTimeout(() => setShowShareSuccess(false), 2000);
      });
    } else {
      // Clipboard fallback
      navigator.clipboard.writeText(shareText);
      setShowShareSuccess(true);
      setTimeout(() => setShowShareSuccess(false), 2000);
    }
  };

  const goToRelatedModule = () => {
    if (tip?.relatedModule) {
      // The modules page opens the module named here (and the module itself
      // remembers your place in it).
      try { localStorage.setItem('returnToModule', String(tip.relatedModule)); } catch { /* opens the list instead */ }
      setCurrentView('modules');
    }
  };

  if (!tip) return null;

  // Category colors
  const categoryColors = {
    science: 'from-blue-500/10 to-purple-500/10 border-blue-500/30',
    practice: 'from-green-500/10 to-emerald-500/10 border-green-500/30',
    reflection: 'from-orange-500/10 to-yellow-500/10 border-orange-500/30',
    challenge: 'from-pink-500/10 to-red-500/10 border-pink-500/30',
    reminder: 'from-teal-500/10 to-cyan-500/10 border-teal-500/30'
  };

  const categoryEmojis = {
    science: '🧠',
    practice: '💪',
    reflection: '💭',
    challenge: '🎯',
    reminder: '📚'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className={`bg-gradient-to-r ${categoryColors[tip.category]} border relative overflow-hidden`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-white flex-1">
              <span className="text-2xl flex-shrink-0">{categoryEmojis[tip.category]}</span>
              <span className="text-base sm:text-lg">{tip.title}</span>
            </CardTitle>
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {new Date().toLocaleDateString('en-AU', {
              weekday: 'long',
              month: 'long',
              day: 'numeric'
            })}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Main Tip */}
          <div className="text-slate-200 leading-relaxed">
            {tip.tip}
          </div>

          {/* Action */}
          {tip.action && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="p-3 bg-white/5 rounded-lg border-l-2 border-teal-400"
            >
              <div className="text-sm text-teal-300 font-medium mb-1">💡 Try This:</div>
              <div className="text-sm text-slate-300">{tip.action}</div>
            </motion.div>
          )}

          {/* Actions Bar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 pt-2">
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShare();
                }}
                className="text-slate-400 hover:text-white w-full sm:w-auto"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share Tip
              </Button>

              {/* Share Success Toast */}
              <AnimatePresence>
                {showShareSuccess && (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    className="absolute left-0 top-10 px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-lg text-sm text-green-400 whitespace-nowrap z-10"
                  >
                    Copied to clipboard!
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {tip.relatedModule && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  goToRelatedModule();
                }}
                className="text-teal-400 hover:text-teal-300 w-full sm:w-auto"
              >
                Learn More
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default TipOfTheDay;
